import React, { useState, useEffect } from "react";
import {
  Card,
  Checkbox,
  Row,
  Col,
  Typography,
  Tag,
  Divider,
  Statistic,
  Select,
  Space,
  Empty,
} from "antd";
import formatIndianNumber from "../../../../../utils/formatIndianNumber";

const { Text } = Typography;
const { Option } = Select;

const SubBDFProductionCost = ({
  allSubBDFData, // New prop to receive all subBDF records
  onIncludeSubBDFCostsChange,
  onSubBDFSelectionChange,
}) => {
  const [includeSubBDFCosts, setIncludeSubBDFCosts] = useState(false);
  const [selectedSubBDFs, setSelectedSubBDFs] = useState([]);
  const [totalPrintingCharge, setTotalPrintingCharge] = useState(0);
  const [totalMountingCharge, setTotalMountingCharge] = useState(0);

  // Calculate totals when selections change
  useEffect(() => {
    // Skip calculation if no data or selections
    if (
      !allSubBDFData ||
      allSubBDFData.length === 0 ||
      selectedSubBDFs.length === 0
    ) {
      setTotalPrintingCharge(0);
      setTotalMountingCharge(0);
      return;
    }

    let printingTotal = 0;
    let mountingTotal = 0;

    // Sum up charges from selected subBDFs
    selectedSubBDFs.forEach((selectedId) => {
      const selectedSubBDF = allSubBDFData.find(
        (item) => item.relatedBilling?._id === selectedId
      );

      if (selectedSubBDF && selectedSubBDF.inventoriesData) {
        selectedSubBDF.inventoriesData.forEach((inventory) => {
          printingTotal += Number(inventory.printingCharge) || 0;
          mountingTotal += Number(inventory.mountingCharge) || 0;
        });
      }
    });

    setTotalPrintingCharge(printingTotal);
    setTotalMountingCharge(mountingTotal);
  }, [selectedSubBDFs, allSubBDFData]);

  // Update parent component when costs change
  useEffect(() => {
    if (includeSubBDFCosts) {
      onIncludeSubBDFCostsChange(totalPrintingCharge, totalMountingCharge);
    } else {
      onIncludeSubBDFCostsChange(0, 0);
    }
  }, [
    includeSubBDFCosts,
    totalPrintingCharge,
    totalMountingCharge,
    onIncludeSubBDFCostsChange,
  ]);

  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setIncludeSubBDFCosts(isChecked);
  };

  const handleSubBDFSelection = (selectedIds) => {
    setSelectedSubBDFs(selectedIds);
    onSubBDFSelectionChange(selectedIds);
  };

  // If no data is available, show nothing
  if (!allSubBDFData || allSubBDFData.length === 0) {
    return null;
  }

  const totalCost = totalPrintingCharge + totalMountingCharge;

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Production Costs Available from Sub BDFs</span>
          <Select
            mode="multiple"
            style={{ width: "60%" }}
            placeholder="Select Sub BDFs"
            value={selectedSubBDFs}
            onChange={handleSubBDFSelection}
            optionLabelProp="label"
          >
            {allSubBDFData.map((item) => (
              <Option
                key={item.relatedBilling?._id}
                value={item.relatedBilling?._id}
                label={item.relatedBilling?.salesOrderId?.stamp || "Unknown"}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>
                    {item.relatedBilling?.salesOrderId?.stamp || "Unknown"}
                  </span>
                  <span>
                    {formatIndianNumber(
                      item.inventoriesData?.reduce(
                        (sum, inv) =>
                          sum +
                          (Number(inv.printingCharge) || 0) +
                          (Number(inv.mountingCharge) || 0),
                        0
                      ) || 0
                    )}
                  </span>
                </div>
              </Option>
            ))}
          </Select>
        </div>
      }
      bordered={true}
      style={{
        marginBottom: "20px",
        borderRadius: "6px",
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.05)",
      }}
    >
      {selectedSubBDFs.length > 0 ? (
        <>
          <Row gutter={12}>
            <Col span={8}>
              <Card
                size="small"
                style={{
                  background: "#fafafa",
                  borderRadius: "4px",
                  border: "1px solid #f0f0f0",
                }}
              >
                <Statistic
                  title="Printing Charge"
                  value={formatIndianNumber(totalPrintingCharge)}
                  valueStyle={{ fontSize: "16px" }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card
                size="small"
                style={{
                  background: "#fafafa",
                  borderRadius: "4px",
                  border: "1px solid #f0f0f0",
                }}
              >
                <Statistic
                  title="Mounting Charge"
                  value={formatIndianNumber(totalMountingCharge)}
                  valueStyle={{ fontSize: "16px" }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card
                size="small"
                style={{
                  background: "#f5f5f5",
                  borderRadius: "4px",
                  border: "1px solid #e8e8e8",
                }}
              >
                <Statistic
                  title="Total Production Cost"
                  value={formatIndianNumber(totalCost)}
                  valueStyle={{
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                />
              </Card>
            </Col>
          </Row>

          <Divider style={{ margin: "16px 0 12px 0" }} />

          <Space>
            {selectedSubBDFs.map((id) => {
              const item = allSubBDFData.find(
                (item) => item.relatedBilling?._id === id
              );
              return item ? (
                <Tag key={id} color="blue">
                  {item.relatedBilling?.salesOrderId?.stamp || "Unknown"}
                </Tag>
              ) : null;
            })}
          </Space>

          <Divider style={{ margin: "16px 0 12px 0" }} />

          <Checkbox
            checked={includeSubBDFCosts}
            onChange={handleCheckboxChange}
            style={{ fontSize: "14px" }}
          >
            <Text strong>
              Include these production costs in the current bill
            </Text>
          </Checkbox>
        </>
      ) : (
        <Empty description="Select Sub BDFs to view production costs" />
      )}
    </Card>
  );
};

export default SubBDFProductionCost;
